import { Stack } from 'component-library';
import { FC, HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { ConservationBenchmarkType, TrendLabel as TrendLabelEnum } from '@/api/rest/resources/conservation';
import { ChartLegendBar } from '@/components/Charts';
import { CustomActiveBar } from '@/components/Charts/components/CustomActiveBar';
import { ChartLegendBarItem } from '@/components/Charts/components/types';
import { ChartProps } from '@/components/Charts/types';
import { minMaxAxisDomain } from '@/components/Charts/utils';
import { BenchmarkPill } from '@/components/Pill/ConservationPill';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { useGetConservationBenchmarks } from '@/pages/shared/projects/pages/project/pages/conservation/BU/hooks/useGetConservationBenchmarks';
import { getBenchmarkForConservationFact } from '@/pages/shared/projects/pages/project/pages/conservation/BU/utils/getBenchmarkForConservationFact';
import {
  Bar,
  Chart,
  ChartTooltipTextPrimary,
  ChartTooltipTextSecondary,
  ComposedChart,
  TooltipProps,
  useTimeseriesXAxisProps,
} from '@/pages/shared/projects/pages/project/pages/conservation/shared/components/Chart';
import {
  RenderBenchmarkReferenceLine,
  RenderTrendLine,
  TrendLabel,
} from '@/pages/shared/projects/pages/project/pages/conservation/shared/components/ReferenceLine';
import { printYear } from '@/utils/formatting/date';

const HABITAT_INTACTNESS_LABEL = 'shared.ncaDetail.habitatIntactness.evolutionBU.statsHeading';

const chartColors = {
  hi: {
    bar: {
      fill: '#A9BD89',
    },
    line: {
      stroke: '#004C3D',
      dot: '#474D45',
    },
  },
} as const;

export type HIChartData = {
  hiBar: number | null;
  hiLine: number | null;
  date: Date;
  benchmark: number | null;
};

type HabitatIntactnessChart = HTMLAttributes<HTMLDivElement> & {
  data: HIChartData[];
  benchmark: ConservationBenchmarkType | null;
  trendLabel: TrendLabelEnum | null;
  yAxisUpperBound?: number;
} & Omit<ChartProps, 'data'>;

export const HabitatIntactnessHistoricalChart: FC<HabitatIntactnessChart> = ({
  data,
  benchmark,
  trendLabel,
  yAxisUpperBound,
  ...delegated
}) => {
  const { t } = useTranslation();

  const xAxisProps = useTimeseriesXAxisProps(data);
  const benchmarks = useGetConservationBenchmarks(yAxisUpperBound);

  const yAxisTickFormatter = yAxisUpperBound === 1 ? (value: number) => value.toFixed(2) : undefined;

  return (
    <>
      <Chart.Container
        {...delegated}
        data={data}
        config={{
          hiBar: {
            label: t(HABITAT_INTACTNESS_LABEL),
            color: chartColors.hi.bar.fill,
          },
          hiLine: {
            label: t(HABITAT_INTACTNESS_LABEL),
            color: chartColors.hi.line.stroke,
          },
        }}
        data-testid='habitat-intactness-chart'
      >
        <ComposedChart data={data}>
          <Bar
            isAnimationActive={false}
            fill={chartColors.hi.bar.fill}
            dataKey='hiBar'
            activeBar={<CustomActiveBar fill={chartColors.hi.bar.fill} />}
            barSize={20}
            className='hi-bar'
          />
          {RenderTrendLine({
            dataKey: 'hiLine',
            stroke: chartColors.hi.line.stroke,
            label: (props) =>
              props.index === data.length - 1 && trendLabel ? (
                <TrendLabel
                  trend={trendLabel}
                  lineColor={chartColors.hi.line.stroke}
                  strokeWidth={2}
                  infoPopoverProps={{
                    title: t(`shared.projects.project.conservation.trendLabel.${trendLabel}`),
                    body: t(`shared.projects.project.conservation.explainers.tooltip.trendLabel.${trendLabel}`),
                  }}
                  {...props}
                />
              ) : (
                <></>
              ),
            dotFill: chartColors.hi.line.dot,
          })}
          {benchmark &&
            RenderBenchmarkReferenceLine({
              value: benchmarks[`${benchmark}`],
              benchmark,
              infoPopoverProps: {
                title: t(`shared.projects.project.conservation.benchmark.${benchmark}`),
                body: t(`shared.projects.project.conservation.explainers.tooltip.benchmark.${benchmark}`),
              },
            })}
          <Chart.XAxis {...xAxisProps} padding={{ left: 40, right: 100 }} className='hi-x-axis' />
          <Chart.YAxis
            domain={minMaxAxisDomain(0.98, 1.02, 0, yAxisUpperBound)}
            className='hi-y-axis'
            tickFormatter={yAxisTickFormatter}
          >
            <Chart.YAxisLabel>{t('shared.ncaDetail.habitatIntactness.evolutionBU.value')}</Chart.YAxisLabel>
          </Chart.YAxis>
          <Chart.Tooltip cursor={false} content={(props) => <CustomTooltip benchmarks={benchmarks} {...props} />} />
        </ComposedChart>
      </Chart.Container>
      <CustomLegendBar />
    </>
  );
};

const CustomLegendBar = () => {
  const { t } = useTranslation();

  const legendBarItems: ChartLegendBarItem[] = [
    {
      label: t(HABITAT_INTACTNESS_LABEL),
      color: chartColors.hi.bar.fill,
      shape: 'column',
    },
    {
      label: t('shared.ncaDetail.habitatIntactness.evolutionBU.trend'),
      color: chartColors.hi.line.stroke,
      shape: 'line',
    },
  ];

  return (
    <ChartLegendBar items={legendBarItems} className='sm:ml-[28px]' data-testid='habitat-intactness-chart-legend' />
  );
};

const CustomTooltip = ({
  active,
  payload,
  label,
  benchmarks,
}: TooltipProps<ValueType, NameType> & { benchmarks: Record<ConservationBenchmarkType, number> }) => {
  const { t } = useTranslation();

  if (!active || !payload?.length) return null;

  const linePayload = payload.find((data) => data.dataKey === 'hiLine');
  const hiDisplayValue =
    linePayload?.value !== null && linePayload?.value !== undefined
      ? getDisplayNumber(linePayload.value as string, window.navigator.language)
      : t('global.analysis.noData');

  const benchmark = getBenchmarkForConservationFact({
    fact: { value: linePayload?.value as number | null },
    benchmarks,
  });

  return (
    <Stack direction='col' spacing={1.5} className='rounded-md bg-white-100 px-2 py-1 elevation-1'>
      <Stack spacing={2}>
        <ChartTooltipTextSecondary>{printYear(label)}</ChartTooltipTextSecondary>
        {benchmark && <BenchmarkPill className='typography-body2' benchmark={benchmark} />}
        <Stack spacing={8} direction='row'>
          <ChartTooltipTextPrimary>{t(HABITAT_INTACTNESS_LABEL)}</ChartTooltipTextPrimary>
          <ChartTooltipTextPrimary>{hiDisplayValue}</ChartTooltipTextPrimary>
        </Stack>
      </Stack>
    </Stack>
  );
};

export { CustomTooltip as CustomTooltipHabitatIntactness };
